import { useState, CSSProperties, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, CheckCircle, XCircle, Euro, TrendingUp, Calendar, ChevronLeft, ChevronRight, Filter, X } from "lucide-react";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { useAuth } from "@/_core/hooks/useAuth";
import { useIsMobile } from "@/hooks/useMobile";
import { hasAnyRole } from "@/lib/roles";
import { TenantLogo } from "@/components/TenantLogo";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { getLoginUrl } from "@/const";
import { Link } from "wouter"

export default function NotificationLogs() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <TenantLogo className="h-16 w-auto" />
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight text-center text-foreground">
              Admin-Bereich
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Bitte melden Sie sich an, um auf den Admin-Bereich zuzugreifen.
            </p>
          </div>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full bg-primary hover:bg-primary/90"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  if (!hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <h1 className="text-2xl font-semibold tracking-tight text-center text-foreground">
              Zugriff verweigert
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Sie haben keine Berechtigung, auf den Admin-Bereich zuzugreifen.
            </p>
          </div>
          <Link href="/">
            <Button>Zur Startseite</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style=
        {{
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <NotificationLogsContent />
    </SidebarProvider>
  );
}

function NotificationLogsContent() {
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState<{
    channel?: 'sms' | 'email';
    startDate?: string;
    endDate?: string;
    notificationType?: string;
    success?: boolean;
    limit: number;
    offset: number;
  }>({
    limit: 20,
    offset: 0,
  });

  const [showFilters, setShowFilters] = useState(false);

  const { data: stats, isLoading: statsLoading } = trpc.sms.getStats.useQuery();
  const { data: logsData, isLoading: logsLoading } = trpc.sms.getLogs.useQuery(filters);

  const notificationTypeLabels: Record<string, string> = {
    cancellation: "Terminabsage",
    reactivation: "Reaktivierung",
    waitlist_promotion: "Warteliste",
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePrevPage = () => {
    setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - f.limit) }));
  };

  const handleNextPage = () => {
    if (logsData && filters.offset + filters.limit < logsData.total) {
      setFilters((f) => ({ ...f, offset: f.offset + f.limit }));
    }
  };

  const clearFilters = () => {
    setFilters({
      limit: 20,
      offset: 0,
    });
  };

  const hasActiveFilters = filters.channel || filters.startDate || filters.endDate || filters.notificationType || filters.success !== undefined;

  return (
    <SidebarInset>
      {isMobile && (
        <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
            <span className="tracking-tight text-foreground font-medium">
              Benachrichtigungsprotokoll
            </span>
          </div>
        </div>
      )}
      <main className="flex-1 p-6">
      <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Benachrichtigungsprotokoll</h1>
        <p className="text-muted-foreground">Übersicht aller gesendeten Benachrichtigungen (SMS & E-Mail)</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamt gesendet</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalSent ?? 0}</div>
            <p className="text-xs text-muted-foreground">Erfolgreiche Benachrichtigungen</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fehlgeschlagen</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats?.totalFailed ?? 0}</div>
            <p className="text-xs text-muted-foreground">Nicht zugestellt</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamtkosten</CardTitle>
            <Euro className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(stats?.totalCost ?? 0).toFixed(2)} €</div>
            <p className="text-xs text-muted-foreground">Alle Zeit</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Dieser Monat</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.thisMonth?.sent ?? 0}</div>
            <p className="text-xs text-muted-foreground">
              {(stats?.thisMonth?.cost ?? 0).toFixed(2)} € Kosten
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Filter</CardTitle>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Zurücksetzen
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-1" />
                {showFilters ? "Ausblenden" : "Anzeigen"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent className="pt-0">
            <div className="grid gap-4 md:grid-cols-5">
              <div className="space-y-2">
                <Label>Kanal</Label>
                <Select
                  value={filters.channel || "all"}
                  onValueChange={(v) =>
                    setFilters((f) => ({ ...f, channel: v === "all" ? undefined : (v as 'sms' | 'email'), offset: 0 }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alle Kanäle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kanäle</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">E-Mail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Von</Label>
                <Input
                  type="date"
                  value={filters.startDate || ""}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, startDate: e.target.value || undefined, offset: 0 }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Bis</Label>
                <Input
                  type="date"
                  value={filters.endDate || ""}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, endDate: e.target.value || undefined, offset: 0 }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select
                  value={filters.notificationType || "all"}
                  onValueChange={(v) =>
                    setFilters((f) => ({ ...f, notificationType: v === "all" ? undefined : v, offset: 0 }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alle Typen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Typen</SelectItem>
                    <SelectItem value="cancellation">Terminabsage</SelectItem>
                    <SelectItem value="reactivation">Reaktivierung</SelectItem>
                    <SelectItem value="waitlist_promotion">Warteliste</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={filters.success === undefined ? "all" : filters.success ? "success" : "failed"}
                  onValueChange={(v) =>
                    setFilters((f) => ({
                      ...f,
                      success: v === "all" ? undefined : v === "success",
                      offset: 0,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Alle Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="success">Erfolgreich</SelectItem>
                    <SelectItem value="failed">Fehlgeschlagen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Benachrichtigungsverlauf</CardTitle>
            <div className="text-sm text-muted-foreground">
              {logsData?.total ?? 0} Einträge
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Laden...</div>
          ) : !logsData?.logs.length ? (
            <div className="text-center py-8 text-muted-foreground">
              Keine SMS-Einträge gefunden
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium">Datum</th>
                      <th className="text-left py-3 px-2 font-medium">Kanal</th>
                      <th className="text-left py-3 px-2 font-medium">Empfänger</th>
                      <th className="text-left py-3 px-2 font-medium">Typ</th>
                      <th className="text-left py-3 px-2 font-medium">Nachricht</th>
                      <th className="text-left py-3 px-2 font-medium">Status</th>
                      <th className="text-right py-3 px-2 font-medium">Kosten</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData.logs.map((log) => (
                      <tr key={log.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2 text-sm">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant={log.channel === 'sms' ? 'default' : 'secondary'}>
                            {log.channel === 'sms' ? 'SMS' : 'E-Mail'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <div className="font-medium">{log.recipientName}</div>
                          <div className="text-sm text-muted-foreground">{log.recipient}</div>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline">
                            {notificationTypeLabels[log.notificationType] || log.notificationType}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 max-w-xs">
                          <div className="text-sm truncate" title={log.message}>
                            {log.message}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          {log.success ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Gesendet
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Fehler
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right text-sm">
                          {log.cost ? `${parseFloat(log.cost).toFixed(3)} €` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {logsData.logs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium">{log.recipientName}</div>
                        <div className="text-sm text-muted-foreground">{log.recipient}</div>
                      </div>
                      {log.success ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Gesendet
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Fehler
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                      <Calendar className="h-3 w-3" />
                      {formatDate(log.createdAt)}
                      <Badge variant={log.channel === 'sms' ? 'default' : 'secondary'} className="ml-auto">
                        {log.channel === 'sms' ? 'SMS' : 'E-Mail'}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline">
                        {notificationTypeLabels[log.notificationType] || log.notificationType}
                      </Badge>
                    </div>
                    <div className="text-sm bg-muted/50 rounded p-2 line-clamp-3">{log.message}</div>
                    {log.cost && (
                      <div className="text-sm text-muted-foreground">
                        Kosten: {parseFloat(log.cost).toFixed(3)} €
                      </div>
                    )}
                    {log.errorMessage && (
                      <div className="text-sm text-destructive">
                        Fehler: {log.errorMessage}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {logsData.total > filters.limit && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Zeige {filters.offset + 1} - {Math.min(filters.offset + filters.limit, logsData.total)} von {logsData.total}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={filters.offset === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={filters.offset + filters.limit >= logsData.total}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      </div>
      </main>
    </SidebarInset>
  );
}
